import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Input, Tag, Form } from 'antd';
import { SearchOutlined, IdcardOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { ActionButtons } from './utils';
import type { ColumnsType } from 'antd/es/table';
import { ModalForm, ProFormText, ProFormSelect, ProFormTextArea } from '@ant-design/pro-components';

interface Expert {
  id: number;
  expert_no: string;
  expert_name: string;
  gender: string;
  phone: string;
  expert_type: string;
  work_unit: string;
  industry: string;
  title: string;
  position: string;
  specialty: string;
  id_card: string;
  bank_name: string;
  bank_account: string;
  remark: string;
  review_status: string;
  create_time: string;
}

export default function AccountExpert() {
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Expert | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchExperts();
  }, []);

  const fetchExperts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/experts', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setExperts(resp.data || []);
      } else {
        message.error(resp?.error || '获取专家列表失败');
      }
    } catch (err) {
      message.error('获取专家列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: Expert) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/experts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchExperts();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const resp = await fetch(`/api/admin/experts/${editingItem?.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('更新成功');
        setModalVisible(false);
        fetchExperts();
        return true;
      } else {
        message.error(resp?.error || '操作失败');
        return false;
      }
    } catch (err) {
      message.error('操作失败');
      return false;
    }
  };

  // 过滤数据
  const filteredData = experts.filter(e => 
    e.expert_no?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    e.expert_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    e.phone?.includes(searchKeyword) ||
    e.work_unit?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  // 导出Excel功能
  const handleExport = () => {
    if (filteredData.length === 0) {
      message.warning('没有可导出的数据');
      return;
    }

    // 定义导出的列和标题
    const headers = [
      '工作单位', '专家证号', '专家姓名', '性别', '行业', '职称', '职务', 
      '电话', '专家类别', '专业', '身份证号', '开户银行', '银行账号', '备注'
    ];
    const fields: (keyof Expert)[] = [
      'work_unit', 'expert_no', 'expert_name', 'gender', 'industry', 'title', 'position',
      'phone', 'expert_type', 'specialty', 'id_card', 'bank_name', 'bank_account', 'remark'
    ];

    // 构建CSV内容（使用BOM支持中文）
    let csvContent = '\uFEFF' + headers.join(',') + '\n';
    
    filteredData.forEach(expert => {
      const row = fields.map(field => {
        const value = expert[field] || '';
        // 处理包含逗号、换行或引号的值
        if (String(value).includes(',') || String(value).includes('\n') || String(value).includes('"')) {
          return `"${String(value).replace(/"/g, '""')}"`;
        }
        return String(value);
      });
      csvContent += row.join(',') + '\n';
    });

    // 创建Blob并下载
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const fileName = `评审人员列表_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.csv`;
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success(`成功导出 ${filteredData.length} 条专家数据`);
  };

  const columns: ColumnsType<Expert> = [
    {
      title: '专家证号',
      dataIndex: 'expert_no',
      key: 'expert_no',
      width: 130,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '姓名',
      dataIndex: 'expert_name',
      key: 'expert_name',
      width: 100,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 60,
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
    },
    {
      title: '专家类别',
      dataIndex: 'expert_type',
      key: 'expert_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === '技术类' ? 'blue' : 'green'}>{type}</Tag>
      )
    },
    {
      title: '工作单位',
      dataIndex: 'work_unit',
      key: 'work_unit',
      width: 180,
      ellipsis: true,
    },
    {
      title: '职称',
      dataIndex: 'title',
      key: 'title',
      width: 120,
    },
    {
      title: '审核状态',
      dataIndex: 'review_status',
      key: 'review_status',
      width: 100,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          '已通过': 'success',
          '审核中': 'processing',
          '已拒绝': 'error',
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_, record) => (
        <ActionButtons
          onEdit={() => handleEdit(record)}
          onDelete={() => handleDelete(record.id)}
          deleteTitle="确定要删除这个专家吗？"
        />
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <IdcardOutlined />
          </div>
          <div>
            <h2>评审人员管理</h2>
            <p>管理所有评审人员</p>
          </div>
        </div>
        
        <Space size="middle">
          <Button 
            type="primary"
            icon={<DownloadOutlined />} 
            onClick={handleExport}
            style={{ borderRadius: 10 }}
          >
            导出评审人员
          </Button>
          <Input
            placeholder="搜索专家..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchExperts}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
        </Space>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <IdcardOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>编辑专家</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={800}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
        submitter={{
          searchConfig: { submitText: '确定', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormText
              name="expert_no"
              label="专家证号"
              placeholder="请输入专家证号"
              rules={[{ required: true, message: '请输入专家证号' }]}
            />
            <ProFormText
              name="expert_name"
              label="姓名"
              placeholder="请输入姓名"
              rules={[{ required: true, message: '请输入姓名' }]}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <ProFormSelect
              name="gender"
              label="性别"
              options={[
                { label: '男', value: '男' },
                { label: '女', value: '女' },
              ]}
              rules={[{ required: true, message: '请选择性别' }]}
            />
            <ProFormText
              name="phone"
              label="电话"
              placeholder="请输入电话"
              rules={[{ required: true, message: '请输入电话' }]}
            />
            <ProFormSelect
              name="expert_type"
              label="专家类别"
              options={[
                { label: '技术类', value: '技术类' },
                { label: '经济类', value: '经济类' },
              ]}
              rules={[{ required: true, message: '请选择专家类别' }]}
            />
          </div>
          <ProFormText
            name="work_unit"
            label="工作单位"
            placeholder="请输入工作单位"
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <ProFormText
              name="industry"
              label="行业"
              placeholder="请输入行业"
            />
            <ProFormText
              name="title"
              label="职称"
              placeholder="请输入职称"
            />
            <ProFormText
              name="position"
              label="职务"
              placeholder="请输入职务"
            />
          </div>
          <ProFormText
            name="specialty"
            label="专业"
            placeholder="请输入专业"
          />
          <ProFormText
            name="id_card"
            label="身份证号"
            placeholder="请输入身份证号"
            rules={[{ required: true, message: '请输入身份证号' }]}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormText
              name="bank_name"
              label="开户银行"
              placeholder="请输入开户银行"
              rules={[{ required: true, message: '请输入开户银行' }]}
            />
            <ProFormText
              name="bank_account"
              label="银行账号"
              placeholder="请输入银行账号"
              rules={[{ required: true, message: '请输入银行账号' }]}
            />
          </div>
          <ProFormSelect
            name="review_status"
            label="审核状态"
            options={[
              { label: '审核中', value: '审核中' },
              { label: '已通过', value: '已通过' },
              { label: '已拒绝', value: '已拒绝' },
            ]}
          />
          <ProFormTextArea
            name="remark"
            label="备注"
            placeholder="请输入备注"
          />
        </div>
      </ModalForm>
    </div>
  );
}

